import './pickupTimelineWrapper.scss';

import { PickupTimelineCard } from '@transferz/fe-component-library/src/components/pickupTimeline/PickupTimelineCard';
import { PickupTimelineStepProps } from '@transferz/fe-component-library/src/components/pickupTimeline/PickupTimelineItem';
import { PickupTimelineNotificationProps } from '@transferz/fe-component-library/src/components/pickupTimeline/PickupTimelineNotification';
import { IJourney } from '@transferz/fe-models';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocalize } from 'localize-react';
import React from 'react';
import { useLocation } from 'wouter';

import { useCustomerSupportPhone } from '../../hooks/useCustomerSupportPhone';
import { useRenderLocalizedTemplate } from '../../hooks/useRenderLocalizedTemplate';
import { IPickupTimeline } from '../../models/pickupTimeline';
import { isJourneyEditable as isJourneyEditableFn } from '../../utils/journeyUtils';

export interface PickupTimelineWrapperProps {
  pickupTimeline: IPickupTimeline;
  journey: IJourney & any;
}

export const PickupTimelineWrapper: React.FC<PickupTimelineWrapperProps> = ({ pickupTimeline, journey }) => {
  const flags = useFlags();
  const { translate: t } = useLocalize();
  const { renderLocalizedTemplate } = useRenderLocalizedTemplate();
  const [location, setLocation] = useLocation();
  const { customerSupportPhone } = useCustomerSupportPhone(journey?.travellerInfo?.residenceCountryCode);

  const faqIsDisabled = window.tz_globalConfigs?.faqIsDisabled;
  const showFlightTrackingLabel = flags?.transferTimelineEnabled && journey?.originLocation?.hub && journey?.journeyExecutionInfo?.pickupTimeAdjustmentMethod === 'FLIGHT_TRACKING';
  const isJourneyEditable = flags?.enableJourneyModification && isJourneyEditableFn(journey);

  const actions: Record<string, PickupTimelineNotificationProps['action']> = {
    INSUFFICIENT_TIME_BETWEEN_FLIGHT_ARRIVAL_AND_PICKUP: isJourneyEditable ? {
      label: t('o_pickup_timeline_action_CHANGE_PICKUP_TIME.0.label'),
      iconUrl: t('o_pickup_timeline_action_CHANGE_PICKUP_TIME.0.icon.filename'),
      onClick: () => {
        setLocation(`${location}/edit`);
      },
    } : !faqIsDisabled ? {
      label: t('o_pickup_timeline_action_CALL_SUPPORT.0.label'),
      iconUrl: t('o_pickup_timeline_action_CALL_SUPPORT.0.icon.filename'),
      onClick: () => {
        window.location.href = `tel:${customerSupportPhone}`;
      },
    } : undefined,
  };

  const pickupTimelineSteps: PickupTimelineStepProps[] = pickupTimeline.steps.map(step => {
    const storyblokKey = `o_pickup_timeline_item_${step.type}.0`;
    return {
      title: t(`${storyblokKey}.title`),
      time: step.timestamp,
      description: renderLocalizedTemplate(`${storyblokKey}.description`, { pickupTimeline, journey }),
      iconUrl: t(`${storyblokKey}.icon.filename`),
    };
  });

  const inPlaceNotifications = pickupTimeline.notifications.map((notification) => ({
    iconUrl: t(`o_pickup_timeline_notification_${notification.type}.0.icon.filename`),
    title: t(`o_pickup_timeline_notification_${notification.type}.0.title`),
    description: renderLocalizedTemplate(`o_pickup_timeline_notification_${notification.type}.0.description`, { pickupTimeline, faqIsDisabled }),
    variant: notification.severity.toLowerCase() as PickupTimelineNotificationProps['variant'],
    action: notification.type && actions[notification.type] ? actions[notification.type] : undefined,
  }));

  return <PickupTimelineCard
    pickupTimelineSteps={pickupTimelineSteps}
    accommodateFlightDelaysInHours={pickupTimeline.accommodateFlightDelaysInHours}
    showFlightTrackingLabel={showFlightTrackingLabel}
    notifications={inPlaceNotifications} />;
};
